/**
 * Validation Utilities
 * 
 * Functions for validating files, documents, and other inputs
 */

// File type constants
const IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/pjp",
  "image/jfif",
  "image/x-jfif",
  "image/png",
  "image/gif",
  "image/webp",
  "image/tiff",
  "image/heic",
  "image/heif",
  "image/avif",
];
const VIDEO_TYPES = ["video/mp4", "video/webm", "video/ogg", "video/quicktime"];
const DOCUMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "application/rtf",
];
const AUDIO_TYPES = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/ogg", "audio/aac", "audio/webm"];

// Size constants (in bytes)
// UPLOAD LIMITS TEMPORARILY DISABLED - To restore, uncomment the size limits below
// const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1 MB
// const MAX_IMAGE_CHAT_SIZE = 10 * 1024 * 1024; // 10 MB
// const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50 MB
// const MAX_VIDEO_CHAT_SIZE = 100 * 1024 * 1024; // 100 MB
// const MAX_DOCUMENT_SIZE = 20 * 1024 * 1024; // 20 MB (aligned with backend)
// const MAX_AUDIO_SIZE = 25 * 1024 * 1024; // 25 MB (aligned with backend)
// const MAX_AUDIO_CHAT_SIZE = 25 * 1024 * 1024; // 25 MB (aligned with backend)
// const MAX_FILE_COUNT = 10;
const MAX_IMAGE_SIZE = Number.MAX_SAFE_INTEGER;
const MAX_IMAGE_CHAT_SIZE = Number.MAX_SAFE_INTEGER;
const MAX_VIDEO_SIZE = Number.MAX_SAFE_INTEGER;
const MAX_VIDEO_CHAT_SIZE = Number.MAX_SAFE_INTEGER;
const MAX_DOCUMENT_SIZE = Number.MAX_SAFE_INTEGER;
const MAX_AUDIO_SIZE = Number.MAX_SAFE_INTEGER;
const MAX_AUDIO_CHAT_SIZE = Number.MAX_SAFE_INTEGER;
const MAX_FILE_COUNT = Number.MAX_SAFE_INTEGER;

// Extension constants (lowercase, without dot)
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "pjpeg", "pjp", "jfif", "png", "gif", "webp", "tiff", "tif", "heic", "heif", "avif", "svg"];
const VIDEO_EXTENSIONS = ["mp4", "webm", "ogg", "ogv", "mov"];
const DOCUMENT_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "rtf"];
const AUDIO_EXTENSIONS = ["mp3", "wav", "ogg", "aac", "webm"];

/**
 * Check if file has allowed extension
 * @param {File} file - File object
 * @param {string[]} allowedExtensions - Array of allowed extensions
 * @returns {boolean} True if extension is allowed
 */
function checkFileExtension(file, allowedExtensions) {
  if (!file || !file.name) return false;
  const parts = file.name.split('.');
  if (parts.length < 2) return false;
  const ext = parts.pop().toLowerCase();
  return allowedExtensions.includes(ext);
}

/**
 * Generic file validation helper
 * @param {File} file - File to validate
 * @param {string[]} validTypes - Array of valid MIME types
 * @param {number} maxSize - Maximum file size in bytes
 * @returns {boolean} True if valid
 */
function validateFile(file, validTypes, maxSize, allowedExtensions = []) {
  if (!file) return false;

  // Check MIME type first
  const isValidType = validTypes.includes(file.type);

  // If MIME type check fails, try extension check as fallback
  // This helps when browser doesn't detect MIME type correctly (e.g. empty type)
  const isValidExtension = !isValidType && allowedExtensions.length > 0
    ? checkFileExtension(file, allowedExtensions)
    : false;

  // UPLOAD LIMITS TEMPORARILY DISABLED - Original: return (isValidType || isValidExtension) && file.size <= maxSize;
  return (isValidType || isValidExtension); // Size check disabled
}

function normalizeFilesInput(files) {
  if (Array.isArray(files)) {
    return files;
  }
  if (files && typeof files.length === "number") {
    return Array.from(files);
  }
  return [];
}

/**
 * Generic file array validation helper for chat
 * @param {File[]} files - Array of files to validate
 * @param {string[]} validTypes - Array of valid MIME types
 * @param {number} maxSize - Maximum file size in bytes
 * @param {string} errorKey - Error message key
 * @returns {string} "success" or error message key
 */
function validateFilesArray(files, validTypes, maxSize, errorKey, allowedExtensions = []) {
  // Normalize FileList or other array-like collections to a plain array
  const normalizedFiles = normalizeFilesInput(files);

  console.debug('[validateFilesArray] Input:', {
    filesCount: normalizedFiles.length,
    validTypes,
    maxSize,
    errorKey,
    allowedExtensions
  });

  if (normalizedFiles.length === 0) {
    console.debug('[validateFilesArray] Failed: files is not array or empty');
    return errorKey;
  }

  // UPLOAD LIMITS TEMPORARILY DISABLED - File count check
  // if (files.length > MAX_FILE_COUNT) {
  //   console.debug('[validateFilesArray] Failed: too many files');
  //   return "chat.maxFileCount";
  // }

  for (const file of normalizedFiles) {
    if (!file) {
      console.debug('[validateFilesArray] Failed: file is null/undefined');
      return errorKey;
    }

    console.debug('[validateFilesArray] Checking file:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    const isValidType = validTypes.includes(file.type);
    const isValidExtension = !isValidType && allowedExtensions.length > 0
      ? checkFileExtension(file, allowedExtensions)
      : false;

    console.debug('[validateFilesArray] Validation result:', {
      isValidType,
      isValidExtension,
      fileType: file.type,
      fileName: file.name
    });

    if (!isValidType && !isValidExtension) {
      console.debug('[validateFilesArray] FAILED: Neither MIME nor extension valid');
      return errorKey;
    }

    // UPLOAD LIMITS TEMPORARILY DISABLED - File size check
    // if (file.size > maxSize) {
    //   console.debug('[validateFilesArray] Failed: file too large', { size: file.size, max: maxSize });
    //   return "chat.maxFileSize";
    // }
  }

  console.debug('[validateFilesArray] SUCCESS');
  return "success";
}

/**
 * Validate image file for chat
 * @param {File[]} files - Array of files to validate
 * @returns {string} "success" or error message key
 */
export function validateImageChat(files) {
  return validateFilesArray(files, IMAGE_TYPES, MAX_IMAGE_CHAT_SIZE, "chat.invalidImageFileType", IMAGE_EXTENSIONS);
}

/**
 * Validate single image file
 * @param {File} file - File to validate
 * @returns {boolean} True if valid
 */
export function validateImage(file) {
  return validateFile(file, IMAGE_TYPES, MAX_IMAGE_SIZE, IMAGE_EXTENSIONS);
}

/**
 * Validate video file
 * @param {File} file - File to validate
 * @returns {boolean} True if valid
 */
export function validateVideo(file) {
  return validateFile(file, VIDEO_TYPES, MAX_VIDEO_SIZE, VIDEO_EXTENSIONS);
}

/**
 * Validate video files for chat
 * @param {File[]} files - Array of files to validate
 * @returns {string} "success" or error message key
 */
export function validateVideoChat(files) {
  return validateFilesArray(files, VIDEO_TYPES, MAX_VIDEO_CHAT_SIZE, "chat.invalidVideoFileType", VIDEO_EXTENSIONS);
}

/**
 * Validate audio file
 * @param {File} file - File to validate
 * @returns {boolean} True if valid
 */
export function validateAudio(file) {
  return validateFile(file, AUDIO_TYPES, MAX_AUDIO_SIZE, AUDIO_EXTENSIONS);
}

/**
 * Validate audio files for chat
 * @param {File[]} files - Array of files to validate
 * @returns {string} "success" or error message key
 */
export function validateAudioChat(files) {
  return validateFilesArray(files, AUDIO_TYPES, MAX_AUDIO_CHAT_SIZE, "chat.invalidAudioFileType", AUDIO_EXTENSIONS);
}

/**
 * Validate document file
 * @param {File} file - File to validate
 * @returns {boolean} True if valid
 */
export function validateDocument(file) {
  return validateFile(file, DOCUMENT_TYPES, MAX_DOCUMENT_SIZE, DOCUMENT_EXTENSIONS);
}

/**
 * Validate document files for chat
 * @param {File[]} files - Array of files to validate
 * @returns {string} "success" or error message key
 */
export function validateDocumentChat(files) {
  return validateFilesArray(files, DOCUMENT_TYPES, MAX_DOCUMENT_SIZE, "chat.invalidDocumentFileType", DOCUMENT_EXTENSIONS);
}

/**
 * Get password validation regex
 * @returns {RegExp} Password validation regex
 */
export function getPasswordRegex() {
  // Only letters (a-z, A-Z) and numbers (0-9), length 6-50
  return /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]{6,50}$/;
}

